import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"

type ProgressRingProps = {
  label: string
  percent: number
  avgPercent: number
  completed?: boolean
  className?: string
}

const RADIUS = 54
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const STROKE_WIDTH = 8
const SIZE = 140
const CENTER = SIZE / 2

export function ProgressRing({
  label,
  percent,
  avgPercent,
  completed,
  className,
}: ProgressRingProps) {
  const { t } = useTranslation("", { keyPrefix: "training.landing" })
  const [animatedPercent, setAnimatedPercent] = useState(0)

  useEffect(() => {
    // Animate from 0 to target
    const timeout = setTimeout(() => setAnimatedPercent(percent), 100)
    return () => clearTimeout(timeout)
  }, [percent])

  const offset = CIRCUMFERENCE - (animatedPercent / 100) * CIRCUMFERENCE

  // Position the average marker on the ring circumference
  const avgAngle = (avgPercent / 100) * 360 // CSS -rotate-90 already starts from top
  const avgRadians = (avgAngle * Math.PI) / 180
  const avgX = CENTER + RADIUS * Math.cos(avgRadians)
  const avgY = CENTER + RADIUS * Math.sin(avgRadians)

  return (
    <div className={cn("flex flex-col items-center gap-2", className)}>
      <div className="relative" style={{ width: SIZE, height: SIZE }}>
        <svg className="-rotate-90" height={SIZE} width={SIZE}>
          {/* Background ring */}
          <circle
            className="text-muted/30"
            cx={CENTER}
            cy={CENTER}
            fill="none"
            r={RADIUS}
            stroke="currentColor"
            strokeWidth={STROKE_WIDTH}
          />
          {/* Progress ring */}
          <circle
            cx={CENTER}
            cy={CENTER}
            fill="none"
            r={RADIUS}
            stroke="currentColor"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={offset}
            strokeLinecap="round"
            strokeWidth={STROKE_WIDTH}
            className={cn(
              "transition-[stroke-dashoffset] duration-1000 ease-out",
              completed ? "text-primary" : "text-primary"
            )}
          />
          {/* Average marker */}
          {avgPercent > 0 && (
            <circle
              className="text-muted-foreground"
              cx={avgX}
              cy={avgY}
              fill="currentColor"
              r={4}
            />
          )}
        </svg>
        {/* Center text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold tabular-nums">{Math.round(animatedPercent)}%</span>
          {completed && (
            <span className="text-primary text-xs font-medium">
              {t("completed_label", "Done!")}
            </span>
          )}
        </div>
      </div>
      <span className="text-sm font-medium">{label}</span>
      {avgPercent > 0 && (
        <span className="text-muted-foreground text-xs">
          {t("platform_average", { pct: Math.round(avgPercent) })}
        </span>
      )}
    </div>
  )
}
